import React, { useEffect, useRef } from "react";

export interface GoogleSignInButtonProps {
  onSuccess: (token: string) => void;
  onError?: (error: string) => void;
  clientId?: string;
  width?: number;
  size?: "large" | "medium" | "small";
  theme?: "outline" | "filled_blue" | "filled_black";
  triggerRef?: React.MutableRefObject<(() => void) | null>;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              width?: number;
            }
          ) => void;
          disableAutoSelect?: () => void;
        };
      };
    };
  }
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onError,
  clientId,
  width = 300,
  size = "large",
  theme = "filled_black",
  triggerRef,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const scriptId = "google-identity-services";
    let isMounted = true;
    let scriptPromise = (window as typeof window & { __gsiScriptPromise?: Promise<void> })
      .__gsiScriptPromise;

    if (!scriptPromise) {
      scriptPromise = new Promise<void>((resolve, reject) => {
        if (document.getElementById(scriptId)) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
        document.head.appendChild(script);
      });

      (window as typeof window & { __gsiScriptPromise?: Promise<void> }).__gsiScriptPromise =
        scriptPromise;
    }

    scriptPromise
      .then(() => {
        if (isMounted) {
          initializeGoogleSignIn();
        }
      })
      .catch((error: Error) => {
        if (isMounted) {
          onError?.(error.message);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clientId, onError, onSuccess, size, theme, triggerRef, width]);

  const initializeGoogleSignIn = () => {
    if (!window.google?.accounts?.id || !buttonRef.current || initializedRef.current) {
      return;
    }

    try {
      if (!clientId) {
        console.warn("Google Client ID not provided. Please provide clientId prop.");
        onError?.("Google authentication is not configured");
        return;
      }
      
      const googleClientId = clientId;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          if (response.credential) {
            onSuccess(response.credential);
          } else {
            onError?.("No credential received from Google");
          }
        },
      });

      // Render the button
      if (buttonRef.current) {
        buttonRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme,
          size: size,
          text: "signin_with",
          width: width,
        });
        initializedRef.current = true;
        if (triggerRef) {
          triggerRef.current = () => {
            const container = buttonRef.current;
            if (!container) {
              return;
            }
            const roleButton = container.querySelector("div[role='button']") as
              | HTMLElement
              | null;
            if (roleButton) {
              roleButton.click();
              return;
            }
            const iframe = container.querySelector("iframe") as HTMLIFrameElement | null;
            if (iframe) {
              iframe.click();
              return;
            }
            const button = container.querySelector("button") as HTMLButtonElement | null;
            if (button) {
              button.click();
            }
          };
        }
      }
    } catch (error) {
      console.error("Error initializing Google Sign-In:", error);
      onError?.(error instanceof Error ? error.message : "Failed to initialize Google Sign-In");
    }
  };

  return (
    <div ref={buttonRef} className="hidden" />
  );
};
